/**
 * Three closely-coupled dialogs that surface after the user manually
 * categorises an expense:
 *
 *   - SmartCategorizeDialogs — dispatcher that picks one of the below
 *     based on the SmartSuggestion variant.
 *   - SmartBulkDialog — "apply this category to the N other rows with
 *     the same description, and optionally save the pattern".
 *   - SmartPatternDialog — "save a pattern for this category so future
 *     uploads auto-categorise".
 *
 * They share `SmartSuggestion`, the `countPatternMatches` helper, and
 * the same "View category" copy + navigation, so colocating them in
 * one file keeps the related logic in one read.
 */

import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { t } from "../../../i18n";
import { cn } from "../../../lib/cn";
import { formatNumericDate } from "../../../lib/format";
import { tokenize } from "../../../lib/similarity";
import { trpc } from "../../../lib/trpc";
import {
  Button,
  Checkbox,
  Dialog,
  Field,
  FieldMessage,
  Icon,
  Input,
  Money,
  useToast,
} from "../../ui";
import type { Expense, SettlementCategory, SmartSuggestion } from "../types";
import { countPatternMatches } from "../utils";

interface SmartCategorizeDialogsProps {
  suggestion: SmartSuggestion | null;
  allExpenses: Expense[];
  categories: SettlementCategory[];
  householdId: string;
  currency: string;
  settlementId: string;
  onClose: () => void;
}

export function SmartCategorizeDialogs({
  suggestion,
  allExpenses,
  categories,
  householdId,
  currency,
  settlementId,
  onClose,
}: SmartCategorizeDialogsProps) {
  if (!suggestion) return null;
  const triggerExpense = allExpenses.find((e) => e.id === suggestion.expenseId);
  const category = categories.find((c) => c.id === suggestion.targetCategoryId);
  // If the user undid the change (or the category disappeared), bail.
  if (!triggerExpense || !category || triggerExpense.categoryId !== suggestion.targetCategoryId) {
    return null;
  }
  if (suggestion.kind === "bulk") {
    return (
      <SmartBulkDialog
        suggestion={suggestion}
        category={category}
        currency={currency}
        settlementId={settlementId}
        householdId={householdId}
        allExpenses={allExpenses}
        onClose={onClose}
      />
    );
  }
  return (
    <SmartPatternDialog
      suggestion={suggestion}
      category={category}
      allExpenses={allExpenses}
      householdId={householdId}
      onClose={onClose}
    />
  );
}

interface SmartBulkDialogProps {
  suggestion: Extract<SmartSuggestion, { kind: "bulk" }>;
  category: SettlementCategory;
  currency: string;
  settlementId: string;
  householdId: string;
  allExpenses: Expense[];
  onClose: () => void;
}

function SmartBulkDialog({
  suggestion,
  category,
  currency,
  settlementId,
  householdId,
  allExpenses,
  onClose,
}: SmartBulkDialogProps) {
  const utils = trpc.useUtils();
  const { show } = useToast();
  const navigate = useNavigate();
  const bulk = trpc.settlement.bulkUpdateCategory.useMutation();
  const updateCategory = trpc.household.updateCategory.useMutation();

  // Seed categories (householdId === null) cannot have patterns appended in
  // place — updateCategory would silently fork them. Hide the pattern UI in
  // that case; the user can still bulk-update the expenses.
  const canEditPattern = category.householdId !== null;
  const initialPattern = suggestion.description.trim().toUpperCase();
  const [savePattern, setSavePattern] = useState(canEditPattern);
  const [patternInput, setPatternInput] = useState(initialPattern);

  // Single-token quick-fill suggestions from the description. Filtered to
  // length ≥ 3 so noise like "as", "no", "kr" doesn't show up. The full
  // description is used as the input pre-fill, so it doesn't need to repeat
  // as a chip.
  const tokens = useMemo(() => {
    const set = new Set<string>();
    for (const tok of tokenize(suggestion.description)) {
      if (tok.length >= 3) set.add(tok.toUpperCase());
    }
    return [...set];
  }, [suggestion.description]);

  const trimmedPattern = patternInput.trim().toUpperCase();
  const patternAlreadyExists = useMemo(
    () => category.matchPatterns.some((p) => p.trim().toUpperCase() === trimmedPattern),
    [category.matchPatterns, trimmedPattern],
  );
  const patternMatchCount = useMemo(
    () => countPatternMatches(trimmedPattern, allExpenses),
    [trimmedPattern, allExpenses],
  );
  const willSavePattern =
    savePattern && canEditPattern && trimmedPattern.length > 0 && !patternAlreadyExists;

  const previewLimit = 5;
  const visibleMatches = suggestion.matches.slice(0, previewLimit);
  const overflow = suggestion.matches.length - visibleMatches.length;

  const isPending = bulk.isPending || updateCategory.isPending;

  const onSubmit = async () => {
    try {
      const result = await bulk.mutateAsync({
        expenseIds: suggestion.matches.map((m) => m.id),
        categoryId: suggestion.targetCategoryId,
      });
      let patternSaveFailed = false;
      if (willSavePattern) {
        try {
          await updateCategory.mutateAsync({
            groupId: householdId,
            categoryId: category.id,
            category: {
              name: category.name,
              icon: category.icon,
              color: category.color,
              defaultShared: category.defaultShared,
              matchPatterns: [...category.matchPatterns, trimmedPattern],
            },
          });
        } catch {
          patternSaveFailed = true;
        }
      }
      show({
        tone: patternSaveFailed ? "warning" : "success",
        title: patternSaveFailed
          ? t.settlement.expenses.smartCategorize.bulk.patternErrorToast
          : willSavePattern
            ? t.settlement.expenses.smartCategorize.bulk.successWithPatternToast(result.updated)
            : t.settlement.expenses.smartCategorize.bulk.successToast(result.updated),
        dismissLabel: t.invitations.toast.dismissLabel,
      });
      utils.settlement.get.invalidate({ settlementId });
      if (willSavePattern && !patternSaveFailed) {
        utils.household.get.invalidate({ groupId: householdId });
      }
      onClose();
    } catch {
      show({
        tone: "warning",
        title: t.settlement.expenses.smartCategorize.bulk.errorToast,
        dismissLabel: t.invitations.toast.dismissLabel,
      });
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      size="md"
      title={t.settlement.expenses.smartCategorize.bulk.title}
      description={t.settlement.expenses.smartCategorize.bulk.body(
        suggestion.matches.length,
        suggestion.description,
        category.name,
      )}
      footer={
        <>
          <Button onClick={onClose} disabled={isPending}>
            {t.settlement.expenses.smartCategorize.bulk.cancel}
          </Button>
          <Button variant="primary" isLoading={isPending} onClick={onSubmit}>
            {t.settlement.expenses.smartCategorize.bulk.submit}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <p className="font-medium text-ink-600 text-xs uppercase tracking-wide">
            {t.settlement.expenses.smartCategorize.bulk.listHeading}
          </p>
          <ul className="space-y-1 text-sm">
            {visibleMatches.map((m) => (
              <li key={m.id} className="flex items-center justify-between gap-3 text-ink-700">
                <span className="text-ink-500 text-xs tabular-nums">
                  {formatNumericDate(m.date)}
                </span>
                <span className="flex-1 truncate">{m.description}</span>
                <Money amount={m.amount} currency={currency} className="text-xs tabular-nums" />
              </li>
            ))}
          </ul>
          {overflow > 0 && (
            <p className="text-ink-500 text-xs">
              {t.settlement.expenses.smartCategorize.bulk.listMoreCount(overflow)}
            </p>
          )}
        </div>

        {canEditPattern && (
          <div className="space-y-3 rounded-md border border-ink-100 bg-ink-50/60 px-3 py-3 dark:bg-ink-100/30">
            <Checkbox
              checked={savePattern}
              onChange={(e) => setSavePattern(e.target.checked)}
              disabled={isPending}
              label={t.settlement.expenses.smartCategorize.bulk.savePatternLabel(category.name)}
              hint={t.settlement.expenses.smartCategorize.bulk.savePatternHint}
            />

            {savePattern && (
              <div className="ml-1.5 space-y-3 border-ink-100 border-l-2 pl-6">
                <Field label={t.settlement.expenses.smartCategorize.pattern.inputLabel}>
                  <Input
                    value={patternInput}
                    onChange={(e) => setPatternInput(e.target.value.toUpperCase())}
                    placeholder={t.settlement.expenses.smartCategorize.pattern.inputPlaceholder}
                    disabled={isPending}
                  />
                </Field>

                <div className="flex items-start gap-2 rounded-md bg-accent-50 px-2.5 py-2 dark:bg-accent-900/20">
                  <Icon
                    name="lightbulb"
                    size="xs"
                    className="mt-0.5 shrink-0 text-accent-600 dark:text-accent-300"
                  />
                  <p className="text-ink-700 text-xs leading-relaxed">
                    {t.settlement.expenses.smartCategorize.bulk.patternTip}
                  </p>
                </div>

                {tokens.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="font-medium text-ink-600 text-xs">
                      {t.settlement.expenses.smartCategorize.pattern.suggestionsLabel}{" "}
                      <span className="font-normal text-ink-400">
                        ({t.settlement.expenses.smartCategorize.pattern.suggestionsHint})
                      </span>
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {tokens.map((token) => {
                        const isActive = token === trimmedPattern;
                        return (
                          <button
                            key={token}
                            type="button"
                            disabled={isPending}
                            onClick={() => setPatternInput(token)}
                            className={cn(
                              "rounded-full border px-2.5 py-0.5 text-xs transition-colors",
                              isActive
                                ? "border-accent-500 bg-accent-50 text-accent-700 dark:bg-accent-900/30 dark:text-accent-200"
                                : "border-ink-200 bg-surface text-ink-700 hover:bg-ink-50 dark:hover:bg-ink-200",
                            )}
                          >
                            {token}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <p className="text-ink-500 text-xs">
                  {trimmedPattern.length === 0
                    ? t.settlement.expenses.smartCategorize.pattern.previewMatchesNone
                    : patternMatchCount === 0
                      ? t.settlement.expenses.smartCategorize.pattern.previewMatchesNone
                      : t.settlement.expenses.smartCategorize.pattern.previewMatchesSome(
                          patternMatchCount,
                        )}
                </p>

                {patternAlreadyExists && trimmedPattern.length > 0 && (
                  <div className="space-y-1.5">
                    <FieldMessage tone="error" className="text-xs">
                      {t.settlement.expenses.smartCategorize.pattern.duplicateNote}
                    </FieldMessage>
                    <button
                      type="button"
                      disabled={isPending}
                      onClick={() => {
                        onClose();
                        navigate({
                          to: "/households/$householdId/$tab",
                          params: { householdId, tab: "categories" },
                        });
                      }}
                      className="inline-flex items-center gap-0.5 font-medium text-accent-700 text-xs hover:text-accent-800 disabled:opacity-50 dark:text-accent-300 dark:hover:text-accent-200"
                    >
                      {t.settlement.expenses.smartCategorize.bulk.viewCategoryLink}
                      <Icon name="chevron-right" size="xs" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
}

interface SmartPatternDialogProps {
  suggestion: Extract<SmartSuggestion, { kind: "pattern" }>;
  category: SettlementCategory;
  allExpenses: Expense[];
  householdId: string;
  onClose: () => void;
}

function SmartPatternDialog({
  suggestion,
  category,
  allExpenses,
  householdId,
  onClose,
}: SmartPatternDialogProps) {
  const utils = trpc.useUtils();
  const { show } = useToast();
  const navigate = useNavigate();
  const updateCategory = trpc.household.updateCategory.useMutation({
    onSuccess: () => {
      show({
        tone: "success",
        title: t.settlement.expenses.smartCategorize.pattern.successToast,
        dismissLabel: t.invitations.toast.dismissLabel,
      });
      utils.household.get.invalidate({ groupId: householdId });
      onClose();
    },
    onError: () => {
      show({
        tone: "warning",
        title: t.settlement.expenses.smartCategorize.pattern.errorToast,
        dismissLabel: t.invitations.toast.dismissLabel,
      });
    },
  });

  // Pre-fill with the full description of the row the user just
  // categorised — same convention as SmartBulkDialog so the two dialogs
  // feel consistent. The user can trim it down (e.g. drop ", OSLO") or
  // click a chip to swap in a single token.
  const triggerExpense = allExpenses.find((e) => e.id === suggestion.expenseId);
  const initialPattern = (triggerExpense?.description ?? "").trim().toUpperCase();
  const [pattern, setPattern] = useState(initialPattern);
  const trimmed = pattern.trim();
  const matchCount = useMemo(
    () => countPatternMatches(trimmed, allExpenses),
    [trimmed, allExpenses],
  );
  const isDuplicate = useMemo(
    () =>
      trimmed.length > 0 &&
      category.matchPatterns.some((p) => p.toUpperCase() === trimmed.toUpperCase()),
    [trimmed, category.matchPatterns],
  );
  const canSubmit = trimmed.length > 0 && !isDuplicate && !updateCategory.isPending;

  const onSubmit = () => {
    if (!canSubmit) return;
    updateCategory.mutate({
      groupId: householdId,
      categoryId: category.id,
      category: {
        name: category.name,
        icon: category.icon,
        color: category.color,
        defaultShared: category.defaultShared,
        matchPatterns: [...category.matchPatterns, trimmed.toUpperCase()],
      },
    });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      size="md"
      title={t.settlement.expenses.smartCategorize.pattern.title}
      footer={
        <>
          <Button onClick={onClose} disabled={updateCategory.isPending}>
            {t.settlement.expenses.smartCategorize.pattern.cancel}
          </Button>
          <Button
            variant="primary"
            isLoading={updateCategory.isPending}
            disabled={!canSubmit}
            onClick={onSubmit}
          >
            {t.settlement.expenses.smartCategorize.pattern.submit}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-ink-700 text-sm">
          {t.settlement.expenses.smartCategorize.pattern.intro(category.name)}
        </p>
        <p className="text-ink-500 text-xs leading-relaxed">
          {t.settlement.expenses.smartCategorize.pattern.guidance}
        </p>

        <Field label={t.settlement.expenses.smartCategorize.pattern.inputLabel}>
          <Input
            value={pattern}
            onChange={(e) => setPattern(e.target.value.toUpperCase())}
            placeholder={t.settlement.expenses.smartCategorize.pattern.inputPlaceholder}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && canSubmit) {
                e.preventDefault();
                onSubmit();
              }
            }}
          />
        </Field>

        <div className="flex items-start gap-2 rounded-md bg-accent-50 px-2.5 py-2 dark:bg-accent-900/20">
          <Icon
            name="lightbulb"
            size="xs"
            className="mt-0.5 shrink-0 text-accent-600 dark:text-accent-300"
          />
          <p className="text-ink-700 text-xs leading-relaxed">
            {t.settlement.expenses.smartCategorize.bulk.patternTip}
          </p>
        </div>

        {suggestion.commonTokens.length > 0 && (
          <div className="space-y-1.5">
            <p className="font-medium text-ink-600 text-xs">
              {t.settlement.expenses.smartCategorize.pattern.suggestionsLabel}{" "}
              <span className="font-normal text-ink-400">
                ({t.settlement.expenses.smartCategorize.pattern.suggestionsHint})
              </span>
            </p>
            <div className="flex flex-wrap gap-2">
              {suggestion.commonTokens.map((token) => {
                const upper = token.toUpperCase();
                const isActive = upper === trimmed.toUpperCase();
                return (
                  <button
                    key={token}
                    type="button"
                    onClick={() => setPattern(upper)}
                    className={cn(
                      "rounded-full border px-2.5 py-0.5 text-xs transition-colors",
                      isActive
                        ? "border-accent-500 bg-accent-50 text-accent-700 dark:bg-accent-900/30 dark:text-accent-200"
                        : "border-ink-200 bg-surface text-ink-700 hover:bg-ink-50 dark:hover:bg-ink-200",
                    )}
                  >
                    {upper}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <p className="text-ink-500 text-xs">
          {matchCount === 0
            ? t.settlement.expenses.smartCategorize.pattern.previewMatchesNone
            : t.settlement.expenses.smartCategorize.pattern.previewMatchesSome(matchCount)}
        </p>

        {isDuplicate && (
          <div className="space-y-1.5">
            <FieldMessage tone="error" className="text-xs">
              {t.settlement.expenses.smartCategorize.pattern.duplicateNote}
            </FieldMessage>
            <button
              type="button"
              disabled={updateCategory.isPending}
              onClick={() => {
                onClose();
                navigate({
                  to: "/households/$householdId/$tab",
                  params: { householdId, tab: "categories" },
                });
              }}
              className="inline-flex items-center gap-0.5 font-medium text-accent-700 text-xs hover:text-accent-800 disabled:opacity-50 dark:text-accent-300 dark:hover:text-accent-200"
            >
              {t.settlement.expenses.smartCategorize.bulk.viewCategoryLink}
              <Icon name="chevron-right" size="xs" />
            </button>
          </div>
        )}
      </div>
    </Dialog>
  );
}
